import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import {
  getBrowserDefaultTimeZone,
  isValidIanaTimeZone,
} from '../utils/dateUtils';

export const TIMEZONE_STORAGE_KEY = 'caosblitz-timezone';

interface TimezoneContextType {
  /** Effective IANA zone used for formatting (override or browser). */
  timezone: string;
  /** User override, or null when following browser default. */
  timezoneOverride: string | null;
  /** Set a specific IANA zone, or 'browser' to clear override. */
  setTimezone: (tz: string | 'browser') => void;
  /** Browser-reported IANA zone at mount (for “use browser” label). */
  browserTimezone: string;
}

const TimezoneContext = createContext<TimezoneContextType>({
  timezone: 'UTC',
  timezoneOverride: null,
  setTimezone: () => {},
  browserTimezone: 'UTC',
});

export const useTimezone = () => useContext(TimezoneContext);

function readStoredOverride(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    if (!raw || !isValidIanaTimeZone(raw)) return null;
    return raw;
  } catch {
    return null;
  }
}

interface TimezoneProviderProps {
  children: ReactNode;
}

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({ children }) => {
  const [browserTimezone] = useState(() => getBrowserDefaultTimeZone());
  const [timezoneOverride, setTimezoneOverride] = useState<string | null>(() => readStoredOverride());

  useEffect(() => {
    try {
      if (timezoneOverride === null) {
        localStorage.removeItem(TIMEZONE_STORAGE_KEY);
      } else {
        localStorage.setItem(TIMEZONE_STORAGE_KEY, timezoneOverride);
      }
    } catch {
      /* ignore quota / private mode */
    }
  }, [timezoneOverride]);

  const setTimezone = useCallback((tz: string | 'browser') => {
    if (tz === 'browser') {
      setTimezoneOverride(null);
      return;
    }
    if (!isValidIanaTimeZone(tz)) return;
    setTimezoneOverride(tz);
  }, []);

  const timezone = useMemo(
    () => (timezoneOverride ?? browserTimezone) || 'UTC',
    [timezoneOverride, browserTimezone]
  );

  const value = useMemo(
    () => ({
      timezone,
      timezoneOverride,
      setTimezone,
      browserTimezone,
    }),
    [timezone, timezoneOverride, setTimezone, browserTimezone]
  );

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};
